import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "content", "filename", "counter"]
  static values = {
    index: { type: Number, default: 0 },
    tabName: { type: String, default: "" },
  }

  connect() {
    this._openFromHash()
  }

  open(event) {
    event.preventDefault()
    this._scanFiles()
    const trigger = event.currentTarget
    const index = this.files.findIndex((f) => f.url === trigger.dataset.lightboxUrl)
    if (index === -1) return
    this.indexValue = index
    this._render()
    this.dialogTarget.showModal()
    document.body.classList.add("overflow-hidden")
    this._updateHash()
  }

  close() {
    this.dialogTarget.close()
    document.body.classList.remove("overflow-hidden")
    this.contentTarget.innerHTML = ""
    this._clearFileFromHash()
  }

  next(event) {
    event?.preventDefault()
    if (this.indexValue < this.files.length - 1) {
      this.indexValue++
      this._render()
      this._updateHash()
    }
  }

  prev(event) {
    event?.preventDefault()
    if (this.indexValue > 0) {
      this.indexValue--
      this._render()
      this._updateHash()
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "ArrowRight") {
      this.next()
    } else if (event.key === "ArrowLeft") {
      this.prev()
    }
  }

  clickBackdrop(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  // Private

  _openFromHash() {
    const hash = window.location.hash.slice(1)
    if (!hash) return

    const slashIndex = hash.indexOf("/")
    if (slashIndex === -1) return

    const tabName = hash.slice(0, slashIndex)
    const filename = decodeURIComponent(hash.slice(slashIndex + 1))

    if (this.tabNameValue && this.tabNameValue !== tabName) return

    this._scanFiles()
    const index = this.files.findIndex((f) => f.filename === filename)
    if (index === -1) return

    this.indexValue = index
    this._render()
    this.dialogTarget.showModal()
    document.body.classList.add("overflow-hidden")
  }

  _updateHash() {
    const file = this.files[this.indexValue]
    if (!file) return

    const tabName = this.tabNameValue || this._currentTabFromHash()
    if (tabName) {
      history.replaceState(null, "", `#${tabName}/${encodeURIComponent(file.filename)}`)
    }
  }

  _clearFileFromHash() {
    const tabName = this.tabNameValue || this._currentTabFromHash()
    if (tabName) {
      history.replaceState(null, "", `#${tabName}`)
    }
  }

  _currentTabFromHash() {
    const hash = window.location.hash.slice(1)
    return hash.split("/")[0] || ""
  }

  _scanFiles() {
    this.files = []
    this.element.querySelectorAll("[data-lightbox-url]").forEach((el) => {
      this.files.push({
        url: el.dataset.lightboxUrl,
        contentType: el.dataset.lightboxContentType,
        filename: el.dataset.lightboxFilename,
      })
    })
  }

  _render() {
    const file = this.files[this.indexValue]
    if (!file) return

    this.filenameTarget.textContent = file.filename
    this.counterTarget.textContent = `${this.indexValue + 1} / ${this.files.length}`

    const type = file.contentType
    let html = ""

    if (type.startsWith("image/")) {
      html = `<img src="${file.url}" alt="${this._escape(file.filename)}" class="max-h-[80vh] max-w-full rounded-lg object-contain" />`
    } else if (type === "application/pdf") {
      html = `<iframe src="${file.url}" class="h-[90vh] w-[90vw] rounded-lg bg-white" frameborder="0"></iframe>`
    } else if (type.startsWith("video/")) {
      html = `<video src="${file.url}" controls class="max-h-[80vh] max-w-full rounded-lg" autoplay></video>`
    } else if (type.startsWith("audio/")) {
      html = `<div class="rounded-xl bg-white p-8"><audio src="${file.url}" controls autoplay class="w-80"></audio></div>`
    } else {
      html = `<div class="rounded-xl bg-white px-8 py-6 text-center">
        <p class="text-sm text-gray-500 mb-3">Podgląd niedostępny</p>
        <a href="${file.url}" class="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500" download>
          Pobierz plik
        </a>
      </div>`
    }

    this.contentTarget.innerHTML = html
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
